import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Typography,
	TextField,
	Paper,
	IconButton,
	Autocomplete,
	Divider,
	CircularProgress,
} from '@mui/material';
import { AddIcon, RemoveIcon } from 'components/icons';
import {
	DateTimePicker,
	AdapterDateFns,
	LocalizationProvider,
} from 'components/mui';
import { v4 as uuidv4 } from 'uuid';
import humanizeDuration from 'humanize-duration';
import { differenceInMilliseconds } from 'date-fns';
import { useNotificationProvider } from 'hooks';
import { addRegisteredPlates } from 'api/canpark/registeredPlates/addRegisteredPlates';
import { getActiveTourKey } from 'components/onboarding/tour/activeTour';
import { setTourGate } from 'components/onboarding/tour/tourGate';
import { signalRegisterPlateSaved } from 'components/onboarding/tour/registerPlateTourState';
import { normalizeLicensePlate } from '../../../CANpark/utils/normalizeLicensePlate';
import { stateProvinceList } from '../../../CANpark/data/stateProvinceList';

const REGISTER_PLATE_TOUR_KEY = 'canpark_register_plate';

const CarRegistrationAddingStage: React.FC<{
	parkingLocationId: string;
	/** Called instead of the real save when the plate-registration tour is active. */
	onSandboxComplete?: () => void;
}> = ({ parkingLocationId, onSandboxComplete }) => {
	// During the tour, start with no state selected so the user must actively pick
	// one (the gate requires it); outside the tour keep the usual default.
	const initialState =
		getActiveTourKey() === REGISTER_PLATE_TOUR_KEY ? '' : 'California';
	const [plates, setPlates] = useState([
		{ id: uuidv4(), licensePlate: '', state: initialState },
	]);

	const [validFrom, setValidFrom] = useState<Date | null>(null);
	const [validUntil, setValidUntil] = useState<Date | null>(null);
	const [loading, setLoading] = useState<boolean>(false);

	const { notifySuccess, notifyError } = useNotificationProvider();

	// Publish field validity so the tour's PLATE / VALIDITY steps can hold their
	// Next button until the user actually fills the fields.
	useEffect(() => {
		if (getActiveTourKey() !== REGISTER_PLATE_TOUR_KEY) return;
		const first = plates[0];
		setTourGate(
			'register-plate-info',
			!!first && first.licensePlate.trim().length > 0 && !!first.state,
		);
	}, [plates]);

	useEffect(() => {
		if (getActiveTourKey() !== REGISTER_PLATE_TOUR_KEY) return;
		setTourGate('register-plate-validity', !!validFrom && !!validUntil);
	}, [validFrom, validUntil]);

	const handlePlateChange = (id: string, newPlate: string) => {
		setPlates((prev) =>
			prev.map((plate) =>
				plate.id === id ? { ...plate, licensePlate: newPlate } : plate,
			),
		);
	};

	const handleStateChange = (id: string, newState: string | null) => {
		setPlates((prev) =>
			prev.map((plate) =>
				plate.id === id ? { ...plate, state: newState || '' } : plate,
			),
		);
	};

	const handleAddPlate = () => {
		setPlates((prev) => [
			...prev,
			{ id: uuidv4(), licensePlate: '', state: 'California' },
		]);
	};

	const handleRemovePlate = (id: string) => {
		setPlates((prev) => prev.filter((plate) => plate.id !== id));
	};

	const handleDateChange = (
		field: 'validFrom' | 'validUntil',
		newValue: Date | null,
	) => {
		if (field === 'validFrom') {
			setValidFrom(newValue);
			if (validUntil && newValue && validUntil < newValue) {
				notifyError('Valid Until must be after Valid From.');
				setValidUntil(null);
			}
		} else {
			if (validFrom && newValue && newValue < validFrom) {
				notifyError('Valid Until must be after Valid From.');
				return;
			}
			setValidUntil(newValue);
		}
	};

	const handleSave = async () => {
		if (loading) return; // Prevent multiple clicks

		// Guided tour: don't register real plates. Just report the sandboxed save
		// and close the dialog — useRegisterPlateTourSync then advances to finish.
		if (getActiveTourKey() === REGISTER_PLATE_TOUR_KEY) {
			notifySuccess('That’s the walkthrough — no plates were registered.');
			signalRegisterPlateSaved();
			onSandboxComplete?.();
			return;
		}

		// Validation checks
		if (plates.some((plate) => !plate.licensePlate)) {
			notifyError('Please fill in all license plates.');
			return;
		}
		if (!validFrom || !validUntil) {
			notifyError('Please select a validity period.');
			return;
		}
		if (validUntil < validFrom) {
			notifyError('Valid Until must be after Valid From.');
			return;
		}

		setLoading(true); // Start loading state

		try {
			const registeredPlates = plates.map((plate) => ({
				id: uuidv4(),
				licenseNumber: plate.licensePlate,
				licenseState: plate.state,
				validFrom,
				validUntil,
				parkingLocationId,
			}));

			await addRegisteredPlates(registeredPlates);
			notifySuccess('Plates saved successfully!');
			reset();
		} catch (error) {
			notifyError('Failed to save plates. Please try again.');
		} finally {
			setLoading(false); // Stop loading state
		}
	};

	const reset = () => {
		setPlates([{ id: uuidv4(), licensePlate: '', state: 'California' }]);
		setValidFrom(null);
		setValidUntil(null);
	};

	return (
		<Box>
			{plates.map((plate) => (
				<Paper
					key={plate.id}
					data-tour="register-plate-fields"
					sx={{
						padding: 1,
						pt: 2,
						marginBottom: 2,
						display: 'flex',
						alignItems: 'center',
						gap: 2,
					}}
				>
					<Box>
						<IconButton
							onClick={() => handleRemovePlate(plate.id)}
							disabled={loading}
						>
							<RemoveIcon />
						</IconButton>
					</Box>

					<TextField
						label="License Plate"
						variant="outlined"
						value={plate.licensePlate}
						onChange={(e) =>
							handlePlateChange(
								plate.id,
								normalizeLicensePlate(e.target.value),
							)
						}
						disabled={loading}
					/>

					<Autocomplete
						options={stateProvinceList}
						getOptionLabel={(option) => option.label}
						value={
							stateProvinceList.find(
								(state) => state.code === plate.state,
							) || null
						}
						onChange={(event, newValue) =>
							handleStateChange(
								plate.id,
								newValue ? newValue.code : '',
							)
						}
						renderInput={(params) => (
							<TextField {...params} label="State/Province" />
						)}
						fullWidth
						sx={{ maxWidth: '400px' }}
						disabled={loading}
					/>
				</Paper>
			))}

			<Button
				variant="outlined"
				startIcon={<AddIcon />}
				onClick={handleAddPlate}
				disabled={loading}
				data-tour="register-plate-add-more"
			>
				Add License Plate
			</Button>

			<Divider
				variant="fullWidth"
				sx={{
					opacity: 0.6,
					backgroundColor: '#E0E0E0',
					width: '100%',
					my: 2,
				}}
			/>

			<Typography fontWeight={300}>Validity Period</Typography>
			<LocalizationProvider dateAdapter={AdapterDateFns}>
				<Box
					data-tour="register-plate-validity"
					sx={{
						display: 'flex',
						gap: 2,
						alignItems: 'center',
						mt: 2,
					}}
				>
					<DateTimePicker
						label="Valid From"
						value={validFrom}
						onChange={(newValue) =>
							handleDateChange('validFrom', newValue)
						}
						renderInput={(params) => <TextField {...params} />}
						disabled={loading}
					/>
					<DateTimePicker
						label="Valid Until"
						value={validUntil}
						onChange={(newValue) =>
							handleDateChange('validUntil', newValue)
						}
						minDate={validFrom} // Prevents selecting a date before "Valid From"
						renderInput={(params) => <TextField {...params} />}
						disabled={loading}
					/>
				</Box>
			</LocalizationProvider>

			<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
				Duration:{' '}
				{validFrom && validUntil
					? humanizeDuration(
							Math.abs(
								differenceInMilliseconds(validUntil, validFrom),
							),
							{
								language: 'en',
								units: ['d', 'h', 'm'],
								round: true,
								delimiter: ', ',
								spacer: ' ',
							},
					  )
					: 'N/A'}
			</Typography>

			<Box
				width={'100%'}
				display={'flex'}
				justifyContent={'center'}
				alignItems={'center'}
				mt={2}
			>
				<Button
					variant="contained"
					sx={{ width: '200px' }}
					onClick={handleSave}
					disabled={loading}
					data-tour="register-plate-save"
				>
					{loading ? <CircularProgress size={24} /> : 'Save'}
				</Button>
			</Box>
		</Box>
	);
};

export default CarRegistrationAddingStage;
